'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, LocateFixed, Pencil, Plus, Rocket, RotateCw, Square, X } from 'lucide-react';
import { api, getApiErrorMessage } from '@/lib/api';
import {
  LAUNCH_REQUEST_TIMEOUT_MS,
  LAUNCH_TIMEOUT_MESSAGE,
  isLaunchRequestTimeout,
  launchAdvisoryNote,
} from '@/lib/launch-request';
import { cn } from '@/lib/utils';
import { cameraFix, type DroneTrack } from '@/lib/drone-telemetry';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import {
  type CoverageReadTally,
  formatElapsed,
  formatMissionTime,
  missionBadgeStatus,
  missionDetailHref,
  missionSortieAirborne,
  missionSortieState,
  type MissionListItem,
} from '../missions/_components/missionListShared';
// The mission page's wording for a refused terminal transition, reused verbatim rather than
// re-phrased here: the two surfaces refuse for the same server reason, so they must not tell an
// operator two different things about it (#391).
import { missionCoverageLock } from '../missions/_components/missionDetailShared';
import { MISSION_TYPE_LABELS, orbitSummary } from '@/lib/mission-type';
import { clampDockSheetWidth } from './CopDockSheet';
import type { CoverageSummary } from './useCopCoverage';
import { CopCoverageBlock } from './CopCoverageBlock';
import type { RotationForecastView } from '../missions/_components/coverageTypes';
import type { CoverageTimelineView } from '@/types/coverage-timeline';
import { COVERAGE_ROW_LABEL, MISSION_ROW_FALLBACK } from '@/lib/copy';
import { COVERAGE_UNREAD_COPY, COVERAGE_UNREAD_TERM } from '@/lib/copy/status';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { zoneAbbreviation } from '@/lib/dateFormat';

/**
 * The three coverage states worth a pixel on the COP, and their words.
 *
 * `pending_station`, `suspended` and `stopped` are absent on purpose. This is a queue row, not
 * the coverage panel: the operator scanning it needs to know whether a station that should be
 * manned IS manned, and the states that answer that are covered, uncovered, and changing hands.
 * The rest are configuration facts, and the mission planner is where those are read.
 */
const COVERAGE_ROW_PRESENTATION: Readonly<Record<string, { label: string; className: string }>> = {
  covered: { label: COVERAGE_ROW_LABEL.covered, className: 'text-status-green-fg' },
  gap: { label: COVERAGE_ROW_LABEL.gap, className: 'text-status-red-fg' },
  handover: { label: COVERAGE_ROW_LABEL.handover, className: 'text-status-amber-fg' },
};

/**
 * How long an armed LAUNCH ignores a confirm press. Matches `LAUNCH_ARM_DWELL_MS`
 * on the canvas and the commit strip's `CONFIRM_DWELL_MS`: the hazard is the same
 * everywhere — arm and confirm share a button position, so a double-click
 * satisfies "two presses" in a single reflex.
 */
const LAUNCH_ARM_DWELL_MS = 600;

/**
 * An armed LAUNCH left alone disarms itself. The canvas can hold an armed state —
 * its LAUNCH sits in a dedicated commit strip the operator is looking at — but
 * here the button lives inside a live list that reorders under WebSocket events,
 * so a forgotten armed row could end up under the cursor exactly where a
 * different row's LAUNCH was a moment ago.
 */
const LAUNCH_ARM_TIMEOUT_MS = 5_000;

/**
 * How long an armed STOP ignores a confirm press. Same dwell as LAUNCH, for
 * the same reason: arm and confirm are one control in one position.
 */
const STOP_ARM_DWELL_MS = 600;

interface CopMissionsPanelProps {
  missions: MissionListItem[];
  /**
   * Live coverage per mission, keyed by mission id. Missions with no coverage attached are
   * simply absent — the map is not a per-mission flag, so a row that carries nothing draws
   * nothing and costs no width.
   */
  coverageByMission: ReadonlyMap<string, CoverageSummary>;
  /**
   * A coverage read has failed and none has succeeded since. Rendered, not swallowed: a row with
   * no coverage marker already means "never attached" and "not a state worth drawing", and
   * silently adding "we could not ask" to that list is how an operator scans a clean-looking
   * queue and concludes nothing is wrong.
   */
  coverageDegraded: boolean;
  /**
   * The queue's own payload could not answer coverage for at least one row (#650).
   *
   * A SECOND PROP RATHER THAN AN OR INTO `coverageDegraded`, and the difference is not cosmetic:
   * `coverageDegraded` gates the second source in `coverageLockFor`, the one that can still answer
   * a mission whose `coverage_attached` came back unread. Folding this fault into it would discard
   * a perfectly healthy `/api/coverage` answer and turn a recoverable unread into a lock — on the
   * panel where a sortie is actually ended. They are two different reads that fail apart, and the
   * banner has to be able to say which.
   *
   * A tally rather than a flag, so the sentence can carry the scope of the fault — one unreadable
   * row among forty is a retry, a queue that can answer none of them is "stop reading the screen".
   */
  coverageUnread: CoverageReadTally;
  /**
   * The SELECTED mission's rotation, from `useCopRotation`. Four props rather than one object so
   * a row that renders no coverage block costs nothing to pass, and so the panel's memoisation
   * sees the same primitives every other prop here is made of.
   *
   * These describe `selectedMissionId` and no other mission. The panel does not check that -
   * `useCopRotation` is keyed on the same id by the page that owns both, and it clears itself
   * during the render the selection changes in, so there is no frame where they disagree.
   */
  rotation: RotationForecastView | null;
  rotationTimeline: CoverageTimelineView | null;
  rotationDegraded: boolean;
  rotationLastReadAt: number | null;
  /** Read the rotation again now - offered in the block's degraded note. */
  onRotationRetry: () => void;
  loading: boolean;
  loadError: boolean;
  /** A poll has succeeded at least once — see `useCopMissionsQueue`. Distinguishes "stale" from
   *  "never had a list", which `missions.length` cannot once a stream frame has inserted a row. */
  everLoaded: boolean;
  canDispatch: boolean;
  selectedMissionId: string | null;
  /**
   * Live tracks for the aircraft on this picture, keyed by drone id. The row
   * needs the report time as well as the position: a fix a minute old is where
   * the aircraft WAS, and the control that would fly the camera at it has to
   * say so instead of pretending (`cameraFix`).
   */
  droneTracks: ReadonlyMap<string, DroneTrack>;
  /**
   * Move the map camera onto the aircraft flying this mission. Camera only —
   * it must not change the mission pick or open the DRONE tab. The operator is
   * working the queue and asked for the map to move, nothing else.
   */
  onCenterOnDrone: (droneId: string) => void;
  /** Row pick. The parent owns focus-the-map and draft route preview. */
  onSelectMission: (missionId: string | null) => void;
  onHoverMission: (missionId: string | null) => void;
  /** A launch was accepted (202) — same contract as the dock overlay's onLaunched. */
  onLaunched: (droneId: string | null, missionId: string | null) => void;
  onNewMission: () => void;
  /**
   * End a running sortie (#574). The server resolves the airframe state and
   * branches: an aircraft in the air is commanded home and the mission completes
   * on touchdown, an aircraft on the ground is not commanded and the mission
   * completes now. Irreversible either way, so it is armed then confirmed.
   */
  onStop: (mission: Pick<MissionListItem, 'id' | 'name' | 'drone_callsign'>) => void;
  /** The mission a stop is in flight for, so its row can say so. */
  stoppingId?: string | null;
  /**
   * Missions this operator stopped whose aircraft is flying home. The row says
   * RETURNING rather than pretending the mission has ended, because it has not —
   * see the state's own note in the COP page for why this is client-local.
   */
  returningMissionIds?: ReadonlySet<string>;
  /**
   * Revise this mission's route on the map. Offered for a draft or a scheduled
   * mission only — an executing one is read-only on every surface, and the row
   * says so rather than offering a control that would be refused.
   */
  onEditRoute: (missionId: string) => void;
  onClose: () => void;
  /** Manual reload for the never-loaded failure state — wired to the queue's refresh. */
  onRetry: () => void;
  /** Shared with the drone/dock sheets — one column, one width. */
  width: number;
  onWidthChange: (width: number) => void;
}

function sectionLabel(text: string, count: number) {
  return (
    <div className="flex items-center gap-2 px-3 pt-3 pb-1.5">
      <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{text}</span>
      <span className="text-[10px] font-mono text-muted-foreground/70 tabular-nums">{count}</span>
    </div>
  );
}

/** Scheduled by soonest departure (unscheduled last); drafts newest-first. */
function bySchedule(a: MissionListItem, b: MissionListItem): number {
  if (a.scheduled_at && b.scheduled_at) return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
  if (a.scheduled_at) return -1;
  if (b.scheduled_at) return 1;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

/**
 * What the row's center-on-aircraft control can do, and what it says it does.
 *
 * Centring is camera-only navigation, as legitimate for a viewer as for an
 * operator (the same reasoning as the dock sheet's GO TO DRONE), so it is not
 * gated on `canDispatch`. What it IS gated on is whether we know where the
 * aircraft is — a control that would move the map to nowhere must not look
 * like it can move the map, and the reason travels with it rather than being
 * left for the operator to guess.
 */
function centerControl(
  m: MissionListItem,
  tracks: ReadonlyMap<string, DroneTrack>,
  nowMs: number,
): { droneId: string | null; usable: boolean; label: string } {
  // The label always names the action first and appends the reason, so the
  // control has the same accessible name whether or not it can act — an
  // operator who tabs onto a dead one hears what it would have done and why it
  // will not.
  const action = `Center the map on ${m.drone_callsign?.trim() || "this mission's aircraft"}`;
  const unavailable = (why: string) => `${action} — unavailable: ${why}`;
  if (!m.assigned_drone_id) {
    // An executing mission launched with an aircraft, so this is a data anomaly
    // rather than a normal state — said plainly rather than hidden.
    return { droneId: null, usable: false, label: unavailable('no aircraft is assigned to this mission') };
  }
  const fix = cameraFix(tracks.get(m.assigned_drone_id), nowMs);
  if (fix.usable) return { droneId: m.assigned_drone_id, usable: true, label: action };
  const why = fix.reason === 'no-track'
    ? 'no telemetry from it on this picture'
    : fix.reason === 'no-gps-fix'
      ? 'it reports no GPS fix'
      : 'link lost — its last position is too old to point at';
  return { droneId: m.assigned_drone_id, usable: false, label: unavailable(why) };
}

/**
 * The COP's mission queue: what is flying, what is about to, and the drafts
 * behind them — list and map as one surface (hover or focus highlights the
 * route, a pick promotes it and flies the camera there). LAUNCH here is the
 * real launch path (`POST /api/missions/:id/launch`), the same server decision
 * the canvas commits through — never a status edit.
 */
export function CopMissionsPanel({
  missions,
  coverageByMission,
  coverageDegraded,
  coverageUnread,
  rotation,
  rotationTimeline,
  rotationDegraded,
  rotationLastReadAt,
  onRotationRetry,
  loading,
  loadError,
  everLoaded,
  canDispatch,
  selectedMissionId,
  droneTracks,
  onCenterOnDrone,
  onSelectMission,
  onHoverMission,
  onLaunched,
  onNewMission,
  onStop,
  stoppingId = null,
  returningMissionIds,
  onEditRoute,
  onClose,
  onRetry,
  width,
  onWidthChange,
}: CopMissionsPanelProps) {
  const timezone = useOrgTimezone();
  const [collapsed, setCollapsed] = useState(false);
  /** Drafts are working papers, not the air picture — closed until asked for. */
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [armed, setArmed] = useState<{ missionId: string; atMs: number } | null>(null);
  /**
   * The mission whose STOP is armed. Held by id, so arming one row cannot
   * arm another — this list reorders under WebSocket events.
   */
  const [armedStop, setArmedStop] = useState<{ missionId: string; atMs: number } | null>(null);
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const listRef = useRef<HTMLDivElement>(null);

  const startWidth = useRef(width);
  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'ew-resize',
    onDragStart: () => {
      startWidth.current = width;
    },
    // Anchored right, so dragging left widens it (same as the dock sheet).
    onDrag: ({ dx }) => onWidthChange(clampDockSheetWidth(startWidth.current - dx, window.innerWidth)),
  });

  const live = missions.filter((m) => m.status === 'in_progress');
  const scheduled = missions.filter((m) => m.status === 'scheduled').toSorted(bySchedule);
  const drafts = missions.filter((m) => m.status === 'draft').toSorted(bySchedule);
  /** First load failed outright: there is NO data, which must never render as "no missions". */
  const neverLoaded = loadError && missions.length === 0;
  /**
   * Rows on screen, but no successful poll behind them (#471) — they arrived on the event stream
   * after a failed first load, so they are a keyhole onto the queue rather than a stale copy of
   * it. Every mission the stream has not mentioned is missing, and nothing on this panel would
   * otherwise say so: `neverLoaded` goes false the moment one frame inserts one row.
   */
  const streamOnly = loadError && !everLoaded && missions.length > 0;

  // 1 Hz elapsed clock, only while something is actually airborne.
  const liveCount = live.length;
  useEffect(() => {
    if (liveCount === 0) return;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [liveCount]);

  // An armed LAUNCH must not survive the world changing under it.
  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(null), LAUNCH_ARM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  // Nor an armed STOP: an operator pulled to the radio must not come back to
  // a row one press from irreversibly ending a sortie.
  useEffect(() => {
    if (!armedStop) return;
    const timer = setTimeout(() => setArmedStop(null), LAUNCH_ARM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [armedStop]);
  /**
   * Why STOP is refused on this row, or null (#391).
   *
   * THIS PANEL IS WHERE A SORTIE IS ACTUALLY ENDED — its STOP is the control an operator
   * reaches for now that the board's live strip is gone (#574). The
   * server now 409s both terminal transitions while continuous coverage is attached, and a
   * covered mission is deliberately `in_progress` with nothing in the air, so it is ALWAYS on
   * this list. Left ungated, the row would draw a COVERED chip and an enabled STOP side by
   * side and answer the press with a red toast — the same defect the mission page's commit strip
   * was fixed for, on the surface an operator actually uses.
   *
   * TWO SOURCES FOR ONE FACT — AND THE HOLE THE SECOND ONE PATCHED IS NOW CLOSED (#420).
   * `coverage_attached` is the decoration the gates themselves are keyed on, and it is the one to
   * trust. It was added here because `applyMissionEvent` REPLACES a queue row with the
   * `mission_event` payload while `missionEventRow` carried no coverage fields, so a colleague
   * merely renaming the mission stripped the flag and silently unlocked this button until the
   * next 30 s poll. `missionEventRow` now carries both fields and `applyMissionEvent` preserves
   * one the frame could not answer, so that erasure is gone.
   *
   * WHY THE OR IS STILL HERE ANYWAY. It used to be poll latency: no coverage route emitted a
   * `mission_event` at all, so this row's `coverage_attached` changed only on the 30 s queue poll
   * while `/api/coverage` refreshed every 20 s. **THAT PREMISE IS SPENT (#471):** attach, detach
   * and force-detach now each emit a `mission_event` (`src/api/coverage.ts`), which is the
   * prerequisite #467 was waiting on. The OR survives only as a SECOND SOURCE for a dropped
   * socket, not as the primary path, and #467 is now free to retire it.
   *
   * IT PASSES THE UNREAD STATE THROUGH RATHER THAN FLATTENING IT (#471). The first version of this
   * computed a plain boolean, so `undefined` became `false` and `missionCoverageLock`'s unread
   * branch was unreachable from the COP — leaving the pre-#471 guess alive at the one gate this
   * file calls "where a sortie is actually ended". The failure that matters is the CORRELATED one,
   * because both sources read the same database: the queue poll's coverage lookup throws AND
   * `/api/coverage` is degraded, so neither can answer, the old code said "not attached", STOP was
   * offered, and `POST /:id/stop` replied 503 `coverage_unavailable`. Refused after the press, on
   * the terminal control. A definite `true` from EITHER source still locks, a definite `false`
   * still opens the door, and only "nobody could answer" reaches the unread sentence.
   *
   * ONLY WHILE THAT SECOND SOURCE IS TRUSTWORTHY. `coverageByMission` is deliberately NOT cleared
   * on a failed poll, and a detach emits no coverage frame — so during a `/api/coverage` outage
   * the map can assert a rotation that was detached minutes ago, and this button would sit dead
   * behind a reason that is no longer true, on the surface with no override. `coverageDegraded`
   * is exactly "a read failed and none has succeeded since", so the stale source is dropped and
   * the lock falls back to the mission's own field.
   *
   * The missions board, which has no second source, is fixed by #420 alone.
   */
  const coverageLockFor = (m: MissionListItem): string | null =>
    missionCoverageLock({
      coverage_attached: m.coverage_attached === true || (!coverageDegraded && coverageByMission.has(m.id))
        ? true
        // Not `false`: an unread field stays unread, so the lock can say so. `m.coverage_attached`
        // is `false` or `undefined` here, and each carries through as itself.
        : m.coverage_attached,
    });

  /** Rows the queue payload could not answer coverage for — see `coverageUnread`. */
  const unreadRows = coverageUnread.unread;

  const armedStopMission = armedStop
    ? missions.find((m) => m.id === armedStop.missionId)
    : undefined;
  // An armed STOP is CLEARED when coverage appears under it, never merely hidden: a
  // colleague attaching a rotation must not leave a press parked on this row that comes back
  // live — with its dwell long expired — the moment they detach again.
  const armedStopLocked = armedStopMission ? coverageLockFor(armedStopMission) !== null : false;
  useEffect(() => {
    if (armedStop
      && (!armedStopMission || armedStopMission.status !== 'in_progress' || armedStopLocked)) {
      setArmedStop(null);
    }
  }, [armedStop, armedStopMission, armedStopLocked]);
  /**
   * WHAT AN ARMED STOP MEANS, ANNOUNCED.
   *
   * The armed state is signalled only by swapping the pressed button's own label while focus
   * stays on it, so nothing spoke: a screen-reader operator could not tell they had armed a
   * terminal action, and the dwell then swallowed the press that would have revealed it. The
   * mission planner's commit strip added exactly this for exactly this reason; the control
   * here does MORE than that one did, because confirming it can also command an aircraft.
   *
   * It names the aircraft when there is a sortie in the air, for the same reason the button's
   * own label does.
   */
  const armedStopAnnouncement = armedStopMission && !armedStopLocked
    ? `Armed. Press Confirm again to end "${armedStopMission.name}".${
      missionSortieAirborne(armedStopMission)
        ? ` ${armedStopMission.drone_callsign || 'The aircraft'} will be commanded home and the mission ends when it lands.`
        : ''
    } This cannot be undone. Keep it running to cancel.`
    : '';

  const armedMission = armed ? missions.find((m) => m.id === armed.missionId) : undefined;
  useEffect(() => {
    if (armed && (!armedMission || armedMission.status !== 'scheduled')) setArmed(null);
  }, [armed, armedMission]);

  // A pick made ON THE MAP must be visible in the list too, or it reads as a
  // no-op: unfold DRAFTS if that is where the row lives, then bring it into view.
  const selectedIsDraft = selectedMissionId !== null
    && drafts.some((m) => m.id === selectedMissionId);
  useEffect(() => {
    if (selectedIsDraft) setDraftsOpen(true);
  }, [selectedIsDraft]);
  useEffect(() => {
    if (!selectedMissionId) return;
    const el = listRef.current?.querySelector(`[data-mission-id="${selectedMissionId}"]`);
    // Optional call: jsdom has no scrollIntoView, and a missing row (still
    // folded, or gone) is simply nothing to scroll to.
    (el as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedMissionId, draftsOpen]);

  // Escape stands an armed LAUNCH down first, then closes the panel — matching
  // the drone workspace, with the same guards: an open dialog (the creation
  // canvas) owns Escape, and so does a text field being edited.
  const armedRef = useRef(armed);
  armedRef.current = armed;
  const armedStopRef = useRef(armedStop);
  armedStopRef.current = armedStop;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="alertdialog"], [role="dialog"]')) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
        return;
      }
      if (armedRef.current) {
        setArmed(null);
        return;
      }
      if (armedStopRef.current) {
        setArmedStop(null);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * Airframes already in the air, and the sortie each is flying.
   *
   * The DURABLE half of telling an operator their plan is about to be — or has already been —
   * orphaned. A same-aircraft clash stopped being a launch refusal (a plan is not a flight, and
   * refusing an emergency dispatch on paperwork was worse), and the refusal had been the only
   * thing that ever said it out loud. A toast at the moment of launch cannot replace it: it is
   * shown once, to whoever pressed the button, and the operator who LOSES the plan never sees
   * it at all.
   *
   * This is derived, not fetched — every row the panel already holds carries `status` and
   * `assigned_drone_id` — so it is correct whenever it is true rather than only at the instant
   * something was commanded, and it appears on the row that is actually affected.
   */
  const flyingByDroneId = useMemo(() => {
    const byDrone = new Map<string, MissionListItem>();
    for (const m of missions) {
      if (m.status === 'in_progress' && m.assigned_drone_id) byDrone.set(m.assigned_drone_id, m);
    }
    return byDrone;
  }, [missions]);

  const handleLaunchPress = async (mission: MissionListItem) => {
    if (launchingId) return;
    if (!armed || armed.missionId !== mission.id) {
      setArmed({ missionId: mission.id, atMs: Date.now() });
      return;
    }
    if (Date.now() - armed.atMs < LAUNCH_ARM_DWELL_MS) return;
    setArmed(null);
    setLaunchingId(mission.id);
    try {
      const res = await api.post<{ data?: { advisories?: unknown } }>(
        `/api/missions/${mission.id}/launch`,
        {},
        { signal: AbortSignal.timeout(LAUNCH_REQUEST_TIMEOUT_MS) },
      );
      // Anything the launch did NOT refuse on, said out loud. The commonest is another mission
      // planned for this same airframe: that stopped being a refusal, so this is now the only
      // thing that tells the operator their sortie has just orphaned somebody's plan.
      const advisory = launchAdvisoryNote(res?.data?.advisories);
      // `toast.warning` when it carries one, and the difference is not decoration: under
      // `richColors` a success toast prints its description in the green this product reserves
      // for "all good", so the only caution in the flow would be typeset as reassurance. The
      // row marker above is the durable channel; this is the one that catches the eye now.
      if (advisory) {
        toast.warning(`${mission.name} — launch commanded`, { description: advisory, duration: 10_000 });
      } else {
        toast.success(`${mission.name} — launch commanded`);
      }
      onLaunched(mission.assigned_drone_id, mission.id);
    } catch (err) {
      // A refusal is the normal case, not an exception: the GO/NO-GO gate says
      // no far more often than the server breaks. The server's reason, verbatim
      // — except a timeout, which is not a refusal but an unknown.
      toast.error(isLaunchRequestTimeout(err) ? LAUNCH_TIMEOUT_MESSAGE : getApiErrorMessage(err, 'Launch failed'));
    } finally {
      setLaunchingId(null);
    }
  };

  const handleStopPress = (mission: MissionListItem) => {
    // THE SYNCHRONOUS HALF OF THE REFUSAL. The button is `aria-disabled` rather than `disabled`
    // (see the control for why), so it stays clickable and nothing in the DOM stops this handler
    // running. In practice the disarm effect above gets there first — it clears the arm on the
    // render after coverage appears, so a second press only re-arms — but that leaves an
    // irreversible transition guarded by an effect alone. This is the line that refuses without
    // waiting for a render.
    if (coverageLockFor(mission)) return;
    // THE IN-FLIGHT LOCK'S SYNCHRONOUS HALF, for exactly the reason above: the control is
    // `aria-disabled` rather than `disabled`, so it stays clickable and this is the line that
    // refuses. One stop at a time — the same single-flight rule LAUNCH runs — and a repeat
    // press on the row whose request is already running is refused by the same test.
    if (stoppingId !== null) return;
    if (!armedStop || armedStop.missionId !== mission.id) {
      setArmedStop({ missionId: mission.id, atMs: Date.now() });
      return;
    }
    // The dwell — a double-click is one reflex, not two decisions.
    if (Date.now() - armedStop.atMs < STOP_ARM_DWELL_MS) return;
    setArmedStop(null);
    // The mission, not just its id: every message this produces has to be able to name the
    // sortie and the AIRCRAFT. Two of these rows stopped in a row otherwise stack two
    // identical toasts, and if one of them was the refusal nothing says which airframe is
    // coming home - on a product whose whole convention is uppercase callsigns so that
    // aircraft can be told apart at a glance.
    onStop({ id: mission.id, name: mission.name, drone_callsign: mission.drone_callsign });
  };

  /**
   * Gaps, split by whether anything can be done about them (#564).
   *
   * A rotation of ONE empties its station on every cycle, by design: it has no relief, so it
   * returns to its dock to be resupplied and comes back on its own. That is a `gap` in
   * `mission_coverage` and it is the correct state — but counting it in the red banner means the
   * banner fires every twenty-odd minutes on a site that is working exactly as configured, and a
   * red alarm that is usually nothing is one a controller learns to scroll past. This is the one
   * indicator D5-S2 leaves carrying coverage failure, so it has to keep meaning something.
   *
   * `member_count` is already on the summary, so telling them apart costs nothing.
   */
  const { gapCount, selfSwapGapCount } = useMemo(() => {
    let unplanned = 0;
    let selfSwap = 0;
    for (const m of missions) {
      const c = coverageByMission.get(m.id);
      if (c?.state !== 'gap') continue;
      if (c.member_count === 1) selfSwap += 1;
      else unplanned += 1;
    }
    return { gapCount: unplanned, selfSwapGapCount: selfSwap };
  }, [missions, coverageByMission]);

  const renderRow = (m: MissionListItem) => {
    const isSelected = m.id === selectedMissionId;
    const isArmed = armed?.missionId === m.id;
    const isLaunching = launchingId === m.id;
    const canLaunch = canDispatch && m.status === 'scheduled';
    // An aircraft flying a route is flying the route the server handed it.
    // Nothing edits that in place, on any surface — so the control is absent,
    // with the reason said out loud rather than a button that gets refused.
    const canEditRoute = canDispatch && (m.status === 'draft' || m.status === 'scheduled');
    // One-shot camera move, offered only where it was asked for: the strip of
    // missions in progress. Deliberately NOT a second FOLLOW — the map's own
    // context menu owns the continuous mode (TOGGLE_FOLLOW in droneMapReducer),
    // and two answers to the same question is one too many.
    const center = m.status === 'in_progress' ? centerControl(m, droneTracks, nowMs) : null;
    // Only for a plan that has NOT launched — an in-progress mission is the sortie doing the
    // occupying, and marking it against itself would be nonsense.
    const occupiedBy = m.status !== 'in_progress' && m.assigned_drone_id
      ? flyingByDroneId.get(m.assigned_drone_id) ?? null
      : null;
    const coverage = coverageByMission.get(m.id) ?? null;
    const coveragePresentation = coverage ? COVERAGE_ROW_PRESENTATION[coverage.state] ?? null : null;
    const coverageLock = coverageLockFor(m);
    // The disarm effect above clears a parked STOP when coverage appears, but it runs AFTER
    // this render — so the armed styling is masked here too, or the row would paint one frame of
    // a red "Confirm — end it" that cannot be pressed.
    const isStopArmed = !coverageLock && armedStop?.missionId === m.id;
    // Only ever true for a mission still in progress — the marker set is pruned
    // against the queue by the page that owns it.
    const isReturning = returningMissionIds?.has(m.id) === true;
    /**
     * DOES THIS PRESS COMMAND AN AIRCRAFT? Said on the row, not only in a `title`.
     *
     * The server owns the real decision, and it has signals the client does not. But the
     * client can tell the LIKELY case, and the one fact that changed about this control when
     * it stopped being a status write is that it may now fly an aircraft home — an operator
     * who has been closing landed sorties with this button for weeks must not learn that from
     * a tooltip. This file already argues the point 50 lines below, about the coverage chip:
     * a `title` is unavailable on touch, unreachable by keyboard and inconsistently exposed.
     *
     * `missionSortieAirborne` is the same predicate the row's elapsed clock and its route lock
     * already read, so a covered station between sorties correctly reads as nothing to command.
     */
    const stopCommandsAircraft = missionSortieAirborne(m);
    const stopInFlight = stoppingId !== null;
    return (
      <li
        key={m.id}
        data-mission-id={m.id}
        onClick={(e) => {
          // Controls inside the row own their clicks (same rule as the board's
          // rows); everything else on the row is a convenience hit target for
          // the name button below, which carries the accessible semantics.
          if (e.target instanceof Element && e.target.closest('a, button')) return;
          onSelectMission(isSelected ? null : m.id);
        }}
        onMouseEnter={() => onHoverMission(m.id)}
        onMouseLeave={() => onHoverMission(null)}
        className={cn(
          'list-none w-full px-3 py-2 border-b divider cursor-pointer transition-colors',
          /*
            A NEUTRAL HOVER, AND NONE AT ALL ON THE ROW THAT IS ALREADY SELECTED.

            This was `hover:bg-accent/50`, and `--accent` is set to the SAME value as `--primary` in
            each theme - #3B82F6 dark, #1D4ED8 light. So the hover was painting this row half
            brand-blue under text coloured `text-foreground` and `text-muted-foreground`, neither of
            which is the `--accent-foreground` that colour is paired with. At the two-line row this
            rule was written for it was a flash; behind the selected row's ~400 px of coverage block
            it is a slab, and it was reported as unreadable in both themes right after the 10% fill
            came off.

            `bg-muted` is the neutral surface lift, and it is what `CopDockRoster` already uses for
            exactly this pair - `hover:bg-muted` on an unselected card, `ring-inset ring-primary`
            on the selected one. AT FULL STRENGTH, not the `/50` this first shipped with: half
            `--muted` measures 1.16:1 against this row, which is not a state change, it is noise.
            The row's hover is not decoration - `onMouseEnter` fires `onHoverMission`, which
            highlights that mission's route on the map, so an operator sweeping the queue has to be
            able to tell which row is lighting up the picture.

            The selected row takes no hover fill at all. Hover means "you could pick this"; once it
            is picked the outline says so, and tinting 400 px of already-selected content answers a
            question nobody is asking.
          */
          !isSelected && 'hover:bg-muted',
          // AN OUTLINE, NOT A FILL. This was `bg-primary/10` with the same left bar, and
          // `--primary` is #3B82F6 in BOTH themes - so the identical blue was laid over a
          // near-black card in dark mode and near-white in light. At 10% that was tolerable behind
          // the two-line row the rule was written for; it is not behind this one. The selected row
          // now carries the whole coverage block, its decision card and its lifecycle strip -
          // roughly 400 px, a third of the panel - and an operator reported the text on it as hard
          // to read in both themes.
          //
          // `ring-inset` paints INSIDE the box, so nothing reflows. A real `border` would add 2 px
          // on four sides and shift every row below the selection each time it moved, in a list
          // that also scrolls the selection into view. The exact string is this COP's existing
          // idiom for a selected thing - see `CopDockRoster.tsx`'s selected dock.
          // `ring-2` AT `/75`, and both numbers are measured rather than chosen.
          //
          // With the fill gone the ring carries most of the perceptual work - the `border-l` bar
          // survives and holds 4.8:1, but it is a 2 px stripe on one edge of a ~400 px block. The
          // ring shipped at `/60`, which measures 2.53:1 dark and 2.62:1 light against this row's
          // composited background: under SC 1.4.11's 3:1, and under the bar this repo already
          // enforces on component boundaries in `theme-tokens-contrast.test.ts`. `/75` measures
          // 3.26:1 and 3.42:1. A 2 px ring is not a fill, so this does not bring back the slab.
          isSelected && 'border-l-2 border-l-primary ring-2 ring-inset ring-primary/75',
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Hold-aware, not a straight status map: a covered station between sorties is
              `in_progress` with nothing in the air, and a green IN PROGRESS chip over an empty
              sky is the misread this whole surface exists to prevent. */}
          <StatusBadge status={missionBadgeStatus(m)} />
          <button
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelectMission(isSelected ? null : m.id)}
            // Keyboard parity with the row hover: focus highlights the route.
            onFocus={() => onHoverMission(m.id)}
            onBlur={() => onHoverMission(null)}
            // The name is `truncate`, and at the panel's new 320 px default it
            // gets roughly 140 px after the status badge and the two icon
            // buttons - about 23 characters, against 63 at the old 560. Without
            // this the only way to read a clipped name is to leave the COP for
            // the planner. Slice 11 is what earns the width back; this is so
            // nothing is unreadable in the meantime.
            title={m.name}
            className="flex-1 min-w-0 truncate text-left text-xs font-medium text-foreground rounded-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            {m.name}
          </button>
          {center && (
            <button
              type="button"
              disabled={!center.usable}
              // No `disabled:pointer-events-none` here: the row delegates its
              // own clicks with `closest('a, button')`, so this button must stay
              // the event target even when dead — otherwise a press on it falls
              // through to the row and toggles the mission pick, which is the
              // one thing centring must never do.
              onClick={() => center.droneId && onCenterOnDrone(center.droneId)}
              title={center.label}
              aria-label={center.label}
              className={cn(
                'shrink-0 -m-1 flex size-6 items-center justify-center rounded-sm transition-colors',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                center.usable
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'cursor-not-allowed text-muted-foreground/40',
              )}
            >
              <LocateFixed className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
            </button>
          )}
          <Link
            href={missionDetailHref(m.id)}
            title="Open in mission planner"
            aria-label={`Open ${m.name} in mission planner`}
            className="shrink-0 p-1 -m-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
          </Link>
        </div>
        {/* The aircraft this plan names is airborne on something else. Amber, on the row, for as
            long as it stays true — not a toast fired once at whoever pressed LAUNCH. */}
        {occupiedBy && (
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-status-amber-fg">
            <AlertTriangle className="size-3 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            <span className="min-w-0">
              <span className="uppercase tracking-wider">{m.drone_callsign || MISSION_ROW_FALLBACK.itsDrone}</span>
              {' is airborne on '}
              <span className="font-medium">{occupiedBy.name}</span>
            </span>
          </div>
        )}
        {/* `flex-wrap`: this line gained a coverage item (#249) and had no wrap, no truncation
            and no min-w-0 inside an `overflow-hidden` row. At the 288 px minimum sheet width the
            content overruns the box, and the items that fall off the end are the waypoint count
            and the distance — silently clipped, no ellipsis, no tooltip. Wrapping costs a second
            line on a narrow panel and keeps every field readable. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          {/* 022. First on the metadata line: what the sortie IS reads before who is flying
              it, and it is the field that tells an operator whether the route below is a
              path, a station or a circle. */}
          <span className="uppercase tracking-wider text-foreground/70">
            {MISSION_TYPE_LABELS[m.type] ?? m.type}
          </span>
          {/* An ORBIT's circle, beside its type. Without it a 5 km orbit and a GO TO POINT are
              the same row - one point, no radius - and the radius is the whole flight path. */}
          {orbitSummary(m) && (
            <span className="font-mono tabular-nums uppercase tracking-wider text-foreground/70">
              {orbitSummary(m)}
            </span>
          )}
          <span className="uppercase tracking-wider">{m.drone_callsign || MISSION_ROW_FALLBACK.noDrone}</span>
          {/* A COUNTING CLOCK IS AN ASSERTION THAT SOMETHING IS FLYING, so it is gated on the
              sortie and not on the status. A covered mission held open between sorties has no
              launch to count from — `updated_at` would tick up from the last status write,
              which is the LANDING, and read as time airborne. It falls through to the
              scheduled-departure column instead, which for a station is simply blank. */}
          {missionSortieState(m) === 'airborne' ? (
            <span className="text-status-green-fg font-mono tabular-nums" title="Elapsed since launch">
              {formatElapsed(m.updated_at, nowMs)}
            </span>
          ) : missionSortieState(m) === 'unknown' ? (
            /* THE THIRD BRANCH #471 ASKED FOR, and this control is why the issue asked for it.
               A COUNTING CLOCK IS THE STRONGEST ASSERTION ON THIS ROW — it does not merely say
               an aircraft is up, it says for how long, in green, ticking. Rendering it from a
               coverage read that FAILED is the #400 misread with a stopwatch on it.
               `formatElapsed` already returns `--:--` for an unusable timestamp, so the dashes
               are this panel's own existing vocabulary for "no number to show", and they sit in
               the same monospace column so nothing reflows when the next poll resolves it. */
            <span
              className="text-muted-foreground font-mono tabular-nums"
              title="Coverage state could not be read — this mission is running, but whether an aircraft is airborne on it is unknown"
            >
              --:--
              {/* THE DASHES ARE INVISIBLE TO A NON-VISUAL READ, twice over. `title` on a plain
                  `<span>` is not focusable, is not reliably exposed by screen readers on a static
                  element, and never fires on touch - so the explanation was carried entirely by a
                  channel some operators do not have. And `formatElapsed` returns the IDENTICAL
                  `--:--` in the airborne branch above for an unusable timestamp, which leaves two
                  meanings in the same four glyphs in the same column, told apart by colour alone
                  (green against muted). That is WCAG 1.4.1, narrow but real.

                  The badge on this row does say COVERAGE UNREAD, which is the substantive
                  mitigation; this makes the clock itself honest rather than decorative. */}
              <span className="sr-only">, airborne state unknown</span>
            </span>
          ) : m.status === 'in_progress' ? (
            <span className="text-status-amber-fg uppercase tracking-wider" title="Coverage is holding this mission open — no aircraft is airborne on it">
              Awaiting next aircraft
            </span>
          ) : (
            <span className="font-mono tabular-nums" title={`Scheduled departure — ${zoneAbbreviation(m.scheduled_at ?? Date.now(), timezone)}`}>
              {formatMissionTime(m.scheduled_at, timezone)}
            </span>
          )}
          {/* THE PRESS DID SOMETHING (#574). STOP on an airborne mission commands the
              aircraft home and deliberately leaves the mission `in_progress` — it is not
              over while the aircraft is in the sky — so without this the row looks
              identical before and after the press for the whole flight home. Amber, not
              green: this is a sortie being recovered, not a station on task.

              It says RETURNING and not "stopping", because that is what the airframe is
              doing and the airframe is the thing the operator is now watching. */}
          {isReturning && (
            <span
              className="uppercase tracking-wider font-medium text-status-amber-fg"
              title="Commanded home — this mission completes when the aircraft is down"
            >
              <span className="sr-only">Aircraft </span>Returning
            </span>
          )}
          {/* Continuous coverage (#249). On the metadata line rather than beside the name: it
              is a property of the sortie in progress, and it must not push the mission name —
              the row's identity — off a narrow panel. A GAP is the one state that reads red on
              this board, because it is the one an operator has to do something about. */}
          {coveragePresentation && (
            <span className={cn('shrink-0 uppercase tracking-wider', coveragePresentation.className)}>
              {/* The accessible name says COVERAGE, because on a row that already carries the
                  mission's own aircraft callsign, a bare "COVERED TALON-1" is two unlabelled
                  callsigns side by side — and when they legitimately differ, which is the point
                  of a rotation, nothing said which was which. A `title` did not fix that: it is
                  unavailable on touch, unreachable by keyboard and inconsistently exposed. */}
              <span className="sr-only">Continuous coverage: </span>
              {coveragePresentation.label}
              {coverage?.covered_by_callsign && (
                <span className="text-muted-foreground"> HELD BY {coverage.covered_by_callsign}</span>
              )}
            </span>
          )}
          <span className="font-mono tabular-nums">{m.waypoint_count} WP</span>
          {m.total_distance_meters !== null && (
            <span className="font-mono tabular-nums">{(m.total_distance_meters / 1000).toFixed(1)} KM</span>
          )}
        </div>
        {/* ENDING A RUNNING SORTIE (#574). One control, both cases: the server
            works out whether the aircraft is in the air and either commands it
            home — the mission then completes on touchdown, through the landing —
            or, with nothing flying, completes the mission and commands nothing.
            It replaced a Complete that was a pure status write, which on an
            airborne mission marked the row completed and left the aircraft up
            there with no mission bound to it. Irreversible either way, so it is
            armed then confirmed like everything else that cannot be taken back. */}
        {canDispatch && m.status === 'in_progress' && (
          // `flex-wrap` with a gap, so the coverage-lock line below sits as its own row rather
          // than as inline text that reflows under the button at narrow panel widths.
          <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <Button
              size="sm"
              variant={isStopArmed ? 'default' : 'outline'}
              // `aria-disabled`, NOT `disabled`, and the reason is the one this file already
              // records for the centre-on-aircraft control above: `Button` carries
              // `disabled:pointer-events-none`, and this row delegates its own clicks with
              // `closest('a, button')` — so a truly disabled button stops being the event target
              // and the press falls through to the row, which re-frames the map on a mission the
              // operator was trying to END. It also keeps the control in the tab order, so the
              // refusal is reachable by keyboard instead of vanishing from it. `handleStopPress`
              // is what actually refuses (#391).
              // `aria-disabled` FOR THE IN-FLIGHT LOCK TOO, and for the same reason as the
              // coverage lock — the paragraph above is not a special case for #391, it is this
              // control's rule. A real `disabled` here made the press fall through to the row and
              // re-frame the map on a DIFFERENT mission, and it dropped keyboard focus to the
              // body at the moment of activation. Harmless while this was a sub-second status
              // write; the window is now a dock plan plus a command with a 10 s ack budget.
              aria-disabled={coverageLock !== null || (stopInFlight && stoppingId !== m.id) || undefined}
              aria-busy={stoppingId === m.id}
              onClick={() => handleStopPress(m)}
              aria-label={
                coverageLock ? `Cannot stop ${m.name} — ${coverageLock}`
                  : stopInFlight && stoppingId !== m.id ? `Cannot stop ${m.name} — another stop is in flight`
                    : undefined
              }
              // A DESCRIPTION, NOT A NAME, and the distinction is WCAG 2.5.3 (Label in Name):
              // the accessible name has to contain the visible label, or a voice-control
              // operator saying what they can read ("stop plus R T H") does not match the
              // control. So the consequence rides in `aria-describedby`, which is announced
              // after the name and matches nothing. The coverage lock still wins when present:
              // a refused control's reason outranks a description of what it would have done.
              aria-describedby={
                coverageLock ? `coverage-lock-${m.id}`
                  : stopCommandsAircraft ? `stop-rth-${m.id}`
                    : undefined
              }
              // Dropped entirely when locked: `aria-label` is the accessible NAME and `title` the
              // DESCRIPTION, so leaving "Stop HARBOUR WATCH" here made a screen reader end the
              // announcement with the opposite of the refusal it had just read out.
              title={coverageLock
                ? undefined
                : isStopArmed
                  ? `Confirm — end ${m.name}. This cannot be undone.`
                  : isReturning
                    ? `${m.drone_callsign || 'The aircraft'} is already returning. Press again only if it is not coming home.`
                    : stopCommandsAircraft
                      ? `Stop ${m.name} — ${m.drone_callsign || 'the aircraft'} is commanded home and the mission ends when it lands`
                      : `Stop ${m.name} — nothing is in the air, so the mission simply closes`}
              className={cn(
                'h-6 px-2 text-[10px] uppercase tracking-wider',
                coverageLock
                  ? 'cursor-not-allowed border-border text-muted-foreground/50 hover:bg-transparent'
                  : isStopArmed
                    ? 'bg-button-destructive hover:bg-button-destructive-hover text-destructive-foreground'
                    // AMBER once it commands an aircraft, green only when it does not. This
                    // button was green when it closed out a landed sortie, which was honest;
                    // green is this product's "on task, all good" and it is the wrong register
                    // for a control that flies an airframe home.
                    : stopCommandsAircraft
                      ? 'border-status-amber-fg/40 text-status-amber-fg hover:bg-status-amber-fg/10'
                      : 'border-status-green-fg/40 text-status-green-fg hover:bg-status-green-fg/10',
              )}
            >
              <Square className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
              {/* The armed label names the CONSEQUENCE, not just the act. "Confirm — end it"
                  was accurate for a status write and is an understatement for a press that
                  also commands an aircraft. */}
              {stoppingId === m.id
                ? 'Stopping…'
                : isStopArmed
                  ? (stopCommandsAircraft ? 'Confirm — end it, fly home' : 'Confirm — end it')
                  : (stopCommandsAircraft ? 'Stop + RTH' : 'Stop')}
            </Button>
            {stopCommandsAircraft && !coverageLock && (
              /* Not `hidden`: an `aria-describedby` target that is `display: none` is still
                 read, but `sr-only` keeps it out of the layout without relying on that. */
              <span id={`stop-rth-${m.id}`} className="sr-only">
                {`${m.drone_callsign || 'The aircraft'} is in the air. Stopping commands it home; the mission ends when it lands.`}
              </span>
            )}
            {isStopArmed && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setArmedStop(null)}
                aria-label={`Keep ${m.name} running`}
                className="h-6 px-2 text-[10px] uppercase tracking-wider border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Keep it
              </Button>
            )}
            {coverageLock && (
              /* A LINK, BECAUSE THE DOOR IT NAMES IS NOT ON THIS SURFACE. There is no detach
                 control anywhere on the COP — it lives in the mission planner's coverage panel —
                 so a bare sentence saying "detach it" left the operator to work out that the
                 route was a 14px external-link icon at the other end of the row, labelled
                 something else. This is the same destination, said in words, on the line where
                 the refusal is read.

                 Amber, not red: nothing is wrong. The station is doing what it was configured to
                 do. `basis-full` so it takes its own line rather than wrapping into an orphan
                 under the button — the row is 336px of content at the default panel width and
                 this sentence does not fit beside a control. */
              <Link
                id={`coverage-lock-${m.id}`}
                href={missionDetailHref(m.id)}
                className="basis-full text-[10px] uppercase tracking-wider text-status-amber-fg underline-offset-2 hover:underline"
              >
                {coverageLock}
              </Link>
            )}
          </div>
        )}
        {/*
          THE ROTATION, UNDER THE ROW IT BELONGS TO, and only for the SELECTED mission.
          Following the selection rather than auto-surfacing whichever mission has a standing
          proposal: every other sheet on this COP follows the selection, and a panel that moves
          itself under the operator's hands is the property the bottom stack was rebuilt to
          remove. The chip on every row is still what says which mission to select.
        */}
        {isSelected && coverage && (
          <CopCoverageBlock
            summary={coverage}
            rotation={rotation}
            timeline={rotationTimeline}
            degraded={rotationDegraded}
            lastReadAt={rotationLastReadAt}
            onRetry={onRotationRetry}
            /* The same gate the five coverage endpoints carry, and the same one this panel
               already uses for LAUNCH and STOP - so an operator who can end a sortie from a row
               can also approve the swap under it, and a viewer gets neither. */
            canCommand={canDispatch}
            onChanged={onRotationRetry}
          />
        )}
        {isSelected && (
          <div className="mt-1.5">
            {canEditRoute ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEditRoute(m.id)}
                title="Revise this route on the map"
                className="h-6 px-2 text-[10px] uppercase tracking-wider border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Pencil className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
                Edit route
              </Button>
            ) : m.status === 'in_progress' ? (
              /* The lock is real either way — the server refuses a route edit on any
                 `in_progress` mission (409) — but only one of these two reasons is true at a
                 time, and "In the air" over an empty station is the false one. */
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {/* Three reasons now, because there are three answers (#471). The lock itself is
                    unconditional — the server 409s a route edit on any `in_progress` mission — so
                    the only question this text answers is WHY, and "In the air" over a station
                    the client could not read is the false one. The third says the lock holds
                    without claiming to know what is above the pad. */}
                {{
                  airborne: 'In the air — route locked',
                  held_open: 'Covered — route locked',
                  // "Running — route locked" was the first wording and it said NOTHING the status
                  // above it does not already say - so this one surface stayed silent about the
                  // degraded read while the mission page's own notice named it. Same head-term as
                  // the badge, so one fact reads as one fact wherever it appears.
                  unknown: `${COVERAGE_UNREAD_TERM} — route locked`,
                }[missionSortieState(m) ?? 'held_open']}
              </span>
            ) : null}
          </div>
        )}
        {canLaunch && (
          <div className="mt-1.5">
            <Button
              size="sm"
              // Any launch in flight disables EVERY row's launch — a press that
              // would be silently swallowed must not look available.
              disabled={launchingId !== null || !m.assigned_drone_id}
              aria-busy={isLaunching}
              onClick={() => void handleLaunchPress(m)}
              title={
                m.assigned_drone_id
                  ? 'Commands the aircraft through the mission launch path — two presses'
                  : 'No aircraft assigned — assign one in the planner first'
              }
              className={cn(
                'h-6 px-2 text-[10px] uppercase tracking-wider',
                isArmed
                  ? 'bg-button-destructive hover:bg-button-destructive-hover text-destructive-foreground'
                  : 'bg-button-primary hover:bg-button-primary-hover text-primary-foreground',
              )}
            >
              <Rocket className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
              {isLaunching ? 'Launching…' : isArmed ? 'Confirm launch' : 'Launch'}
            </Button>
            {isArmed && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setArmed(null)}
                className="ml-1.5 h-6 px-2 text-[10px] uppercase tracking-wider border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Stand down
              </Button>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div
      className={`
        flex flex-col min-h-0 overflow-hidden
        lg:w-[var(--dock-sheet-width)] lg:shrink-0 lg:relative lg:z-auto lg:h-auto
        fixed bottom-0 left-0 right-0 z-40 max-w-[100vw]
        rounded-t-xl border-t border-subtle bg-popover/90 backdrop-blur-sm
        shadow-sheet
        lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:backdrop-blur-none
      `}
    >
      {/* Assertive, and outside the list on purpose: the row it describes re-renders and can
          reorder under WebSocket events, and a live region that unmounts mid-announcement
          says nothing at all. */}
      <p role="status" aria-live="assertive" className="sr-only">{armedStopAnnouncement}</p>
      <button
        type="button"
        aria-label="Resize missions panel"
        onPointerDown={onResizePointerDown}
        className="hidden lg:block absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize border-0 bg-transparent p-0 hover:bg-status-blue/30 focus:outline-none focus-visible:bg-primary"
      />

      <button
        type="button"
        className="lg:hidden flex flex-col items-center justify-center gap-1.5 py-2.5 w-full touch-manipulation border-b divider"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand missions panel' : 'Collapse missions panel'}
      >
        <div className="w-10 h-1 rounded-full bg-muted-foreground/40" aria-hidden />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Missions</span>
      </button>

      {/* The mobile drawer is height-capped exactly like the dock sheet: without
          the cap, a long queue grows past the top of the viewport and takes the
          header, the close control and the IN PROGRESS section with it. */}
      <div
        className={cn(
          'flex-1 min-h-0 flex flex-col max-h-[min(60vh,36rem)] lg:max-h-full bg-card/60',
          collapsed && 'hidden lg:flex',
        )}
      >
        <div className="flex items-center justify-between px-3 py-1.5 border-b divider shrink-0">
          <span className="text-[11px] uppercase tracking-wider text-foreground">Mission queue</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close missions panel"
            className="p-1.5 -my-0.5 -mx-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="size-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {loadError && missions.length > 0 && (
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-status-amber-fg bg-status-amber/10 border-b divider shrink-0">
            {streamOnly
              /* "Last known" would be a claim that this list was once complete. It never was —
                 see `streamOnly`. This says what the operator is actually looking at, and that
                 the absence of a mission below is not evidence it does not exist. */
              ? 'Mission queue never loaded — live updates only, list incomplete'
              : 'Mission data degraded — showing last known'}
          </div>
        )}

        {/* TWO COVERAGE READS, AND THEY FAIL APART (#650). This one is the `/api/coverage` poll;
            the one below is the queue payload's own lookup, which comes back 200 with the fields
            omitted and so trips neither `loadError` nor `coverageDegraded`.

            WHEN BOTH ARE BLIND, ONLY ONE BAR — AND IT IS THE OTHER ONE. `coverageByMission.size
            === 0` beside an unread payload is the CORRELATED failure `coverageLockFor` below calls
            "the failure that matters": neither source can answer, so STOP is refused on every row.
            Both sentences describe it, but only the one below carries an instruction, so that is
            the one that survives. "Markers may be out of date" is a different fact and is never
            suppressed — a stale marker is not an unreadable row, and both are worth saying. */}
        {coverageDegraded && !(unreadRows > 0 && coverageByMission.size === 0) && (
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-status-amber-fg bg-status-amber/10 border-b divider shrink-0">
            {coverageByMission.size > 0
              ? 'Coverage degraded — markers may be out of date'
              : 'Coverage unavailable — no row below is showing its coverage'}
          </div>
        )}

        {unreadRows > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-status-amber-fg bg-status-amber/10 border-b divider shrink-0"
          >
            {COVERAGE_UNREAD_COPY.boardNotice(coverageUnread)}
          </div>
        )}

        {/* GAP is the one coverage state that requires an operator to do something, and a row
            carrying it can be scrolled out of the list, is not sorted to the top, and (per
            D5-S2) raises no alert and makes no sound. A count on the header is the cheapest
            thing that keeps it findable without scanning. */}
        {gapCount > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-status-red-fg bg-status-red/10 border-b divider shrink-0"
          >
            {gapCount} coverage {gapCount === 1 ? 'gap' : 'gaps'} — no aircraft on station
          </div>
        )}

        {/* The planned kind, stated in the app's standby hue rather than its alarm one. Still
            surfaced — an operator should know a station is currently empty — but it needs no
            decision from them, and it ends by itself. */}
        {selfSwapGapCount > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-status-blue-fg bg-status-blue/10 border-b divider shrink-0"
          >
            {selfSwapGapCount} single-aircraft {selfSwapGapCount === 1 ? 'station' : 'stations'}
            {' '}resupplying — coverage resumes on its own
          </div>
        )}

        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-6 text-xs text-muted-foreground">Loading missions…</div>
          ) : neverLoaded ? (
            // No data at all is not an empty queue — never assert "no missions"
            // on a failed load.
            <div className="px-3 py-6 space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-status-amber-fg">
                Mission data unavailable
              </p>
              <p className="text-xs text-muted-foreground">The mission queue could not be loaded.</p>
              <Button
                size="sm"
                variant="outline"
                onClick={onRetry}
                className="h-7 px-2.5 text-[10px] uppercase tracking-wider border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <RotateCw className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
                Retry
              </Button>
            </div>
          ) : missions.length === 0 ? (
            <div className="px-3 py-6 text-xs text-muted-foreground">
              No draft, scheduled or in-progress missions.
            </div>
          ) : (
            <>
              {live.length > 0 && (
                <section aria-label="Missions in progress">
                  {sectionLabel('In progress', live.length)}
                  <ul className="m-0 p-0">{live.map(renderRow)}</ul>
                </section>
              )}
              {scheduled.length > 0 && (
                <section aria-label="Scheduled missions">
                  {sectionLabel('Scheduled', scheduled.length)}
                  <ul className="m-0 p-0">{scheduled.map(renderRow)}</ul>
                </section>
              )}
              {drafts.length > 0 && (
                <section aria-label="Draft missions">
                  <button
                    type="button"
                    onClick={() => setDraftsOpen((o) => !o)}
                    aria-expanded={draftsOpen}
                    className="w-full flex items-center gap-2 px-3 pt-3 pb-1.5 text-left"
                  >
                    {draftsOpen
                      ? <ChevronDown className="size-3 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
                      : <ChevronRight className="size-3 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />}
                    <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Drafts</span>
                    <span className="text-[10px] font-mono text-muted-foreground/70 tabular-nums">{drafts.length}</span>
                  </button>
                  {draftsOpen && <ul className="m-0 p-0">{drafts.map(renderRow)}</ul>}
                </section>
              )}
            </>
          )}
        </div>

        <div className="shrink-0 border-t divider px-3 py-2 flex items-center gap-2">
          {canDispatch && (
            <Button
              size="sm"
              onClick={onNewMission}
              className="h-7 px-2.5 text-[10px] uppercase tracking-wider bg-button-primary hover:bg-button-primary-hover text-primary-foreground"
            >
              <Plus className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
              New mission
            </Button>
          )}
          <Link
            href="/missions"
            className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            All missions →
          </Link>
        </div>
      </div>
    </div>
  );
}
